import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { StockItem } from "@/lib/accounting/types";
import {
  StockCategory, StockBatch, StockMovement, StockTransfer, MovementType,
  FIFOLayer, fifoConsume, fifoValuation,
} from "@/lib/inventory/types";
import { useAccounting } from "./AccountingContext";
import { useAuth } from "./AuthContext";
import { useCompany } from "./CompanyContext";
import { supabase } from "@/integrations/supabase/client";
import { syncItemStockToDb, reconcileAllInventory } from "@/lib/inventory/engine";

interface InventoryContextType {
  categories: StockCategory[];
  batches: StockBatch[];
  movements: StockMovement[];
  transfers: StockTransfer[];
  addCategory: (name: string, description?: string, parentCategory?: string) => Promise<StockCategory>;
  addBatch: (itemId: string, batchNumber: string, qty: number, rate: number, mfgDate?: string, expiryDate?: string) => Promise<StockBatch>;
  getBatchesByItem: (itemId: string) => StockBatch[];
  getExpiringBatches: (withinDays?: number) => StockBatch[];
  recordMovement: (itemId: string, type: MovementType, qty: number, rate: number, batchId?: string, reference?: string, narration?: string) => Promise<StockMovement>;
  getMovementsByItem: (itemId: string) => StockMovement[];
  adjustStock: (itemId: string, qty: number, rate: number, direction: "in" | "out", narration?: string, batchId?: string) => Promise<void>;
  transferStock: (fromItemId: string, toItemId: string, qty: number, rate: number, narration?: string) => Promise<void>;
  reconcileInventory: () => Promise<any>;
  getFIFOLayers: (itemId: string) => FIFOLayer[];
  getFIFOValuation: (itemId: string) => number;
  getLowStockItems: () => StockItem[];
  getStockSummary: () => {
    totalItems: number;
    goodsCount: number;
    servicesCount: number;
    totalValue: number;
    lowStock: number;
    expiringBatches: number;
    totalBatches: number;
  };
}

const InventoryContext = createContext<InventoryContextType | null>(null);

function mapDbCategory(row: any): StockCategory {
  return {
    id: row.id,
    name: row.name,
    description: row.description || undefined,
    parentCategory: row.parent_category || undefined,
    createdAt: row.created_at,
  };
}

function mapDbBatch(row: any): StockBatch {
  return {
    id: row.id,
    stockItemId: row.stock_item_id,
    batchNumber: row.batch_number,
    manufacturingDate: row.manufacturing_date || undefined,
    expiryDate: row.expiry_date || undefined,
    quantity: Number(row.quantity),
    rate: Number(row.rate),
    value: Number(row.value),
    status: row.status || "Active",
    createdAt: row.created_at,
  };
}

function mapDbMovement(row: any): StockMovement {
  return {
    id: row.id,
    stockItemId: row.stock_item_id,
    batchId: row.batch_id || undefined,
    type: row.type as MovementType,
    date: row.date,
    quantity: Number(row.quantity),
    rate: Number(row.rate),
    value: Number(row.value),
    reference: row.reference || undefined,
    narration: row.narration || undefined,
    createdBy: row.created_by || "system",
    createdAt: row.created_at,
  };
}

export function InventoryProvider({ children }: { children: ReactNode }) {
  const { stockItems, refreshStockItems } = useAccounting();
  const { user, isAuthenticated } = useAuth();
  const { activeCompany } = useCompany();
  const [categories, setCategories] = useState<StockCategory[]>([]);
  const [batches, setBatches] = useState<StockBatch[]>([]);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [transfers, setTransfers] = useState<StockTransfer[]>([]);

  // Load inventory records from database
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setCategories([]);
      setBatches([]);
      setMovements([]);
      return;
    }

    const loadInventoryData = async () => {
      try {
        const companyId = activeCompany?.id || user.company;

        let catQuery = supabase.from("stock_categories").select("*").order("name");
        let batchQuery = supabase.from("stock_batches").select("*").order("created_at", { ascending: false });
        let movQuery = supabase.from("stock_movements").select("*").order("created_at", { ascending: false });

        if (companyId) {
          catQuery = catQuery.eq("company_id", companyId);
          batchQuery = batchQuery.eq("company_id", companyId);
          movQuery = movQuery.eq("company_id", companyId);
        }

        const [catRes, batchRes, movRes] = await Promise.all([
          catQuery,
          batchQuery,
          movQuery,
        ]);

        if (catRes.data) setCategories(catRes.data.map(mapDbCategory));
        if (batchRes.data) setBatches(batchRes.data.map(mapDbBatch));
        if (movRes.data) setMovements(movRes.data.map(mapDbMovement));
      } catch (err) {
        console.error("[InventoryContext] Error loading inventory from DB:", err);
      }
    };

    loadInventoryData();
  }, [isAuthenticated, user?.id, activeCompany?.id]);

  const addCategory = useCallback(async (name: string, description?: string, parentCategory?: string) => {
    const id = `sc-${crypto.randomUUID().slice(0, 8)}`;
    const now = new Date().toISOString();
    const cat: StockCategory = { id, name, description, parentCategory, createdAt: now };
    setCategories((p) => [...p, cat]);

    const companyId = activeCompany?.id || user?.company || null;
    await supabase.from("stock_categories").insert({
      id,
      company_id: companyId,
      name,
      description: description || null,
      parent_category: parentCategory || null,
    });

    return cat;
  }, [user, activeCompany?.id]);

  const addBatch = useCallback(async (itemId: string, batchNumber: string, qty: number, rate: number, mfgDate?: string, expiryDate?: string) => {
    const id = `b-${crypto.randomUUID().slice(0, 8)}`;
    const now = new Date().toISOString();
    const batch: StockBatch = {
      id,
      stockItemId: itemId,
      batchNumber,
      manufacturingDate: mfgDate,
      expiryDate,
      quantity: qty,
      rate,
      value: qty * rate,
      status: "Active",
      createdAt: now,
    };
    setBatches((p) => [...p, batch]);

    const companyId = activeCompany?.id || user?.company || null;
    await supabase.from("stock_batches").insert({
      id,
      company_id: companyId,
      stock_item_id: itemId,
      batch_number: batchNumber,
      manufacturing_date: mfgDate || null,
      expiry_date: expiryDate || null,
      quantity: qty,
      rate,
      value: qty * rate,
      status: "Active",
    });

    return batch;
  }, [user, activeCompany?.id]);

  const getBatchesByItem = useCallback((itemId: string) => batches.filter((b) => b.stockItemId === itemId), [batches]);

  const getExpiringBatches = useCallback((withinDays = 30) => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + withinDays);
    return batches.filter((b) => b.expiryDate && b.status === "Active" && new Date(b.expiryDate) <= cutoff);
  }, [batches]);

  const recordMovement = useCallback(async (itemId: string, type: MovementType, qty: number, rate: number, batchId?: string, reference?: string, narration?: string) => {
    const id = `mv-${crypto.randomUUID().slice(0, 8)}`;
    const date = new Date().toISOString().split("T")[0];
    const now = new Date().toISOString();
    const mv: StockMovement = {
      id,
      stockItemId: itemId,
      batchId,
      type,
      date,
      quantity: qty,
      rate,
      value: qty * rate,
      reference,
      narration,
      createdBy: user?.id || "unknown",
      createdAt: now,
    };
    setMovements((p) => [mv, ...p]);

    const companyId = activeCompany?.id || user?.company || null;
    await supabase.from("stock_movements").insert({
      id,
      company_id: companyId,
      stock_item_id: itemId,
      batch_id: batchId || null,
      type,
      date,
      quantity: qty,
      rate,
      value: qty * rate,
      reference: reference || null,
      narration: narration || null,
      created_by: user?.id || null,
    });

    if (companyId) {
      await syncItemStockToDb(itemId, companyId);
      await refreshStockItems(companyId);
    }

    if (batchId) {
      setBatches((prev) => prev.map((b) => {
        if (b.id !== batchId) return b;
        const isIn = type.includes("In") || type === "Purchase" || type === "Opening";
        const newQty = isIn ? b.quantity + qty : Math.max(0, b.quantity - qty);
        const newStatus = newQty <= 0 ? "Depleted" : b.status;
        supabase.from("stock_batches").update({
          quantity: newQty,
          value: newQty * b.rate,
          status: newStatus,
        }).eq("id", batchId).then();
        return { ...b, quantity: newQty, value: newQty * b.rate, status: newStatus };
      }));
    }
    return mv;
  }, [user, activeCompany?.id, refreshStockItems]);

  const getMovementsByItem = useCallback((itemId: string) =>
    movements.filter((m) => m.stockItemId === itemId).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), [movements]);

  const adjustStock = useCallback(async (itemId: string, qty: number, rate: number, direction: "in" | "out", narration?: string, batchId?: string) => {
    await recordMovement(itemId, direction === "in" ? "Adjustment-In" : "Adjustment-Out", qty, rate, batchId, `ADJ-${Date.now().toString(36).toUpperCase()}`, narration);
  }, [recordMovement]);

  const transferStock = useCallback(async (fromItemId: string, toItemId: string, qty: number, rate: number, narration?: string) => {
    const ref = `TRF-${Date.now().toString(36).toUpperCase()}`;
    await recordMovement(fromItemId, "Transfer-Out", qty, rate, undefined, ref, narration);
    await recordMovement(toItemId, "Transfer-In", qty, rate, undefined, ref, narration);
    setTransfers((p) => [{
      id: `trf-${crypto.randomUUID().slice(0, 8)}`,
      date: new Date().toISOString().split("T")[0],
      fromItemId,
      toItemId,
      quantity: qty,
      rate,
      value: qty * rate,
      narration,
      createdBy: user?.id || "unknown",
      createdAt: new Date().toISOString(),
    }, ...p]);
  }, [user, recordMovement]);

  const getFIFOLayers = useCallback((itemId: string): FIFOLayer[] => {
    const itemMovements = movements.filter((m) => m.stockItemId === itemId).sort((a, b) => a.date.localeCompare(b.date));
    let layers: FIFOLayer[] = [];
    for (const mv of itemMovements) {
      const isIn = mv.type === "Opening" || mv.type === "Purchase" || mv.type === "Adjustment-In" || mv.type === "Transfer-In";
      if (isIn) layers.push({ batchId: mv.batchId, date: mv.date, quantity: mv.quantity, rate: mv.rate });
      else layers = fifoConsume(layers, mv.quantity).remaining;
    }
    return layers;
  }, [movements]);

  const getFIFOValuation = useCallback((itemId: string) => fifoValuation(getFIFOLayers(itemId)), [getFIFOLayers]);

  const getLowStockItems = useCallback(
    () => stockItems.filter((i) => i.itemType !== "service" && i.reorderLevel && i.currentQty <= i.reorderLevel),
    [stockItems]
  );

  const getStockSummary = useCallback(() => {
    const physicalGoods = stockItems.filter((i) => i.itemType !== "service");
    const services = stockItems.filter((i) => i.itemType === "service");
    return {
      totalItems: stockItems.length,
      goodsCount: physicalGoods.length,
      servicesCount: services.length,
      totalValue: physicalGoods.reduce((s, i) => s + (i.currentValue || 0), 0),
      lowStock: getLowStockItems().length,
      expiringBatches: getExpiringBatches(60).length,
      totalBatches: batches.filter((b) => b.status === "Active").length,
    };
  }, [stockItems, getLowStockItems, getExpiringBatches, batches]);

  const reconcileInventory = useCallback(async () => {
    const companyId = activeCompany?.id || user?.company;
    if (!companyId) return null;
    const res = await reconcileAllInventory(companyId);
    // Reload movements from DB
    const { data: movRows } = await supabase.from("stock_movements").select("*").eq("company_id", companyId).order("created_at", { ascending: false });
    if (movRows) setMovements(movRows.map(mapDbMovement));
    await refreshStockItems(companyId);
    return res;
  }, [activeCompany?.id, user?.company, refreshStockItems]);

  return (
    <InventoryContext.Provider value={{
      categories, batches, movements, transfers,
      addCategory, addBatch, getBatchesByItem, getExpiringBatches,
      recordMovement, getMovementsByItem, adjustStock, transferStock,
      reconcileInventory,
      getFIFOLayers, getFIFOValuation, getLowStockItems, getStockSummary,
    }}>
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory() {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error("useInventory must be used within InventoryProvider");
  return ctx;
}
